/**
 * Employee Web — `/auth/invite?token=...` route. EMP-05 / CC-52.
 *
 * UNAUTHENTICATED (NOT nested under `_auth`) — invitees arriving via email
 * have no session yet.
 *
 * Tenant slug resolution per be-050 §3:
 *   1) `?tenantSlug=...` query string wins (covers local dev, manual override,
 *      staging where the URL template ships query-mode).
 *   2) Subdomain via @linkhr/auth `resolveTenant({ fallbackSlug })` (production:
 *      `acme.hr.linkco.jo` → "acme").
 *   3) Else the form renders the missing-link terminal state (form treats an
 *      empty slug the same as AUTH_INVITE_INVALID).
 *
 * Token validation is delegated to the route's Zod search schema — backend
 * tokens are 32-43 chars; min 20 is a generous lower bound that catches
 * obviously-truncated copy/paste without being brittle to a future format
 * change.
 *
 * On success: navigate to `/me` (employee home — the existing _auth.me route).
 * On request-new-link: navigate to `/login` (no employee-side resend page;
 * users contact HR out-of-band).
 */
import type { JSX } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { resolveTenant } from "@linkhr/auth";
import { InvitePage } from "../features/auth/InvitePage";
import { env } from "../env";

const searchSchema = z.object({
  token: z.string().min(20),
  tenantSlug: z.string().min(1).optional(),
});

export const Route = createFileRoute("/auth/invite")({
  validateSearch: searchSchema,
  component: InviteRouteComponent,
});

function InviteRouteComponent(): JSX.Element {
  const { token, tenantSlug: slugFromQuery } = Route.useSearch();
  const navigate = useNavigate();
  // Query-string slug wins over subdomain (be-050 §3 priority order).
  const tenantSlug = slugFromQuery ?? resolveTenant({ fallbackSlug: env.VITE_DEV_TENANT_SLUG });
  return (
    <InvitePage
      token={token}
      tenantSlug={tenantSlug}
      onSuccess={() => {
        void navigate({ to: "/me" });
      }}
      onRequestNewLink={() => {
        void navigate({ to: "/login" });
      }}
    />
  );
}
